import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../context/UserProvider';
import logo from '../../assets/images/DLM-logo-shadow.svg';
import open from '../../assets/icons/eye-regular.svg';
import closed from '../../assets/icons/eye-slash-regular.svg';
import imagesData from '../../assets/backgrounds/images.json';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';

const imageUrls = imagesData.images.map((image) =>
  require(`../../assets/backgrounds/${image}`)
);

export default function LoginPage() {
  const { login, resetPasswordRequest, redirectLogin } = useContext(UserContext);
  const [loginInput, setLoginInput] = useState({ username: '', password: '' });
  const [resetInput, setResetInput] = useState({ username: '' });
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(15);
  const navigate = useNavigate();
  const [opacity, setOpacity] = useState(1);
  const [backgroundImageIndex, setBackgroundImageIndex] = useState(null);

  const handleBackgroundChange = (direction) => {
    if (
      document.activeElement.tagName !== 'INPUT' &&
      document.activeElement.tagName !== 'TEXTAREA'
    ) {
      setOpacity(0);
      setTimeout(() => {
        setBackgroundImageIndex((prevIndex) => {
          if (direction === 'prev') {
            return (prevIndex - 1 + imageUrls.length) % imageUrls.length;
          } else {
            return (prevIndex + 1) % imageUrls.length;
          }
        });
        setOpacity(1);
      }, 1000);
    }
  };

  const getRandomBackgroundIndex = () => {
    return Math.floor(Math.random() * imageUrls.length);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginFailed(false);
    setErrorMessage('');
    setSuccessMessage('');
    setShowError(false);
    setShowSuccess(false);
    try {
      if (isLogin) {
        await login(loginInput);
      } else {
        await resetPasswordRequest(resetInput.username);
        setSuccessMessage(
          'Please check your email for password reset instructions.'
        );
        setShowSuccess(true);
      }
    } catch (err) {
      setLoginFailed(true);
      setErrorMessage(
        isLogin
          ? 'Incorrect Username or Password.'
          : 'An error occurred. Please try again.'
      );
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendDisabled(true);
    setResendCountdown(15);
    const countdownInterval = setInterval(() => {
      setResendCountdown((prevCountdown) => {
        if (prevCountdown <= 1) {
          clearInterval(countdownInterval);
          setResendDisabled(false);
          return 0;
        }
        return prevCountdown - 1;
      });
    }, 1000);

    try {
      await resetPasswordRequest(resetInput.username);
      setSuccessMessage('Password reset link resent successfully.');
      setShowSuccess(true);
    } catch (err) {
      setErrorMessage('Failed to resend password reset link. Please try again.');
      setShowError(true);
    }
  };

  const preloadImages = async () => {
    const promises = imagesData.images.map((image) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = require(`../../assets/backgrounds/${image}`);
        img.onload = () => {
          resolve();
        };
      });
    });
    await Promise.all(promises);
  };

  const transitionStyles = {
    transition: 'opacity 1s ease-in',
    opacity: opacity,
  };

  const closeNotification = () => {
    setShowError(false);
    setShowSuccess(false);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handleBackgroundChange('prev');
      } else if (e.key === 'ArrowRight') {
        handleBackgroundChange('next');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    preloadImages().then(() => {
      setBackgroundImageIndex(getRandomBackgroundIndex());
    });
    if (redirectLogin()) {
      navigate('/vendors');
    }
  }, [navigate, redirectLogin]);

  return (
    <>
      <div
        className={`fixed left-0 top-0 h-full w-full transition-opacity ${
          backgroundImageIndex !== null ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          ...transitionStyles,
          zIndex: -1,
          backgroundImage: `url(${imageUrls[backgroundImageIndex]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      ></div>
      <div className="flex min-h-screen items-center justify-center bg-transparent px-2 py-16 sm:px-2 lg:px-2">
        <div className="w-full max-w-sm space-y-10 rounded-lg bg-white p-4 shadow-lg">
          <div className="text-center">
            <img
              className="w-6/8 mx-auto my-20 mb-8 h-32"
              src={logo}
              alt="Logo"
            />
          </div>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {isLogin ? (
              <>
                {/* Login Form */}
                <div className="space-y-4 rounded-md">
                  <div>
                    <label htmlFor="login-username" className="sr-only">
                      Username
                    </label>
                    <input
                      id="login-username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      required
                      className="relative mx-auto block w-3/4 appearance-none rounded-md border border-gray-400 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="Username"
                      value={loginInput.username}
                      maxLength="50"
                      onChange={(e) =>
                        setLoginInput({
                          ...loginInput,
                          username: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="relative">
                    <label htmlFor="password" className="sr-only">
                      Password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type={passwordVisible ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      className={`relative mx-auto block w-3/4 appearance-none rounded-md border px-3 py-2 ${
                        loginFailed ? 'border-red-500' : 'border-gray-400'
                      } text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 sm:text-sm`}
                      placeholder="Password"
                      value={loginInput.password}
                      maxLength="256"
                      onChange={(e) =>
                        setLoginInput({
                          ...loginInput,
                          password: e.target.value,
                        })
                      }
                    />
                    <div
                      className="absolute inset-y-0 right-14 flex cursor-pointer items-center"
                      onClick={() => setPasswordVisible(!passwordVisible)}
                    >
                      {passwordVisible ? (
                        <img
                          src={open}
                          alt="Hide password"
                          className="h-6 w-6 text-zinc-200"
                        />
                      ) : (
                        <img
                          src={closed}
                          alt="Show password"
                          className="h-6 w-6 text-zinc-200"
                        />
                      )}
                    </div>
                  </div>
                </div>
                <div className="">
                  <button
                    type="submit"
                    className={`group relative mx-auto flex w-3/4 justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-blue-500 ${
                      isLoading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={isLoading}
                  >
                    Sign in
                  </button>
                </div>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setIsLogin(false)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-500"
                  >
                    Forgot password?
                  </button>
                </div>
              </>
            ) : (
              <>
                {!showSuccess ? (
                  <>
                    <p className="text-center text-sm text-gray-600">
                      Submit your username and check your email for a password
                      reset link.
                    </p>
                    <div className="space-y-4 rounded-md">
                      <div>
                        <label htmlFor="reset-username" className="sr-only">
                          Username
                        </label>
                        <input
                          id="reset-username"
                          name="username"
                          type="text"
                          autoComplete="new-username"
                          required
                          className="relative mx-auto block w-3/4 appearance-none rounded-md border border-gray-400 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          placeholder="Username"
                          value={resetInput.username}
                          maxLength="50"
                          onChange={(e) =>
                            setResetInput({
                              ...resetInput,
                              username: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="">
                      <button
                        type="submit"
                        className={`group relative mx-auto flex w-3/4 justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:ring-blue-500 ${
                          isLoading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={isLoading}
                      >
                        Send
                      </button>
                    </div>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setIsLogin(true)}
                        className="text-sm font-medium text-blue-600 hover:text-blue-500"
                      >
                        Back to login
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      A password reset link has been sent to your email.
                    </p>
                    <div className="mt-6 space-y-4">
                      <button
                        type="button"
                        onClick={handleResend}
                        className={`group relative mx-auto flex w-3/4 justify-center rounded-md bg-blue-400 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 focus:ring-blue-500 ${
                          resendDisabled ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        disabled={resendDisabled}
                      >
                        {resendDisabled
                          ? `Wait ${resendCountdown} seconds...`
                          : 'Resend'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsLogin(true)}
                        className="group relative mx-auto flex w-3/4 justify-center rounded-md bg-white border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:ring-blue-500"
                      >
                        Back to login
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </form>
        </div>
      </div>
      {showSuccess && (
        <SuccessNotification
          show={showSuccess}
          onClose={closeNotification}
          text={successMessage}
        />
      )}
      {showError && (
        <ErrorNotification
          show={showError}
          onClose={closeNotification}
          text={errorMessage}
        />
      )}
    </>
  );
}